<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useQuery } from '@vue/apollo-composable';

import { UErrorView, useError } from '@ugi/core';
import { ListChatTabCountsDocument, ListHelpGuidesDocument } from '@ugi/graphql';

import { useAppBadge } from '@/shared/composables/useAppBadge';
import { useAuth } from '@/shared/composables/useAuth';
import { provideNotifications, useNotifications } from '@/shared/composables/useNotifications';
import { useSwNavigationListener } from '@/shared/composables/useSwNavigationListener';
import { useTheme } from '@/shared/composables/useTheme';
import { useVersionCheck } from '@/shared/composables/useVersionCheck';

import type { NavigationMenuItem } from '@nuxt/ui';

const { error } = useError();
const hasError = computed(() => Boolean(error.value?.statusCode));
const isCriticalError = computed(() => error.value?.critical === true);

const { currentUser, isGranted, logout } = useAuth();

const route = useRoute();

const open = ref(false);

/**
 * Ліва навігація живе рельсою у 48px, а підписи показує панеллю поверх
 * контенту — так само, як Productlane (заміряно: у нього рельса 44px, а
 * розкриття НЕ рухає контент, `main.x` лишається на місці).
 *
 * Різниця з `collapsible` із коробки принципова: там згортання змінює ширину
 * самого сайдбара, тобто контент щоразу перебудовується. Оверлей віддає ті
 * самі 168px назавжди й нічого не смикає.
 *
 * Режим ОДИН: рельса плюс панель на наведення. Тут був ще й закріплений стан
 * із кнопками «закріпити»/«згорнути», і він зберігався в `localStorage`
 * (`main-sidebar-collapsed`). Через це два браузери показували різну
 * навігацію, а той, хто колись натиснув «закріпити», більше ніколи не бачив
 * рельси — саме так це й виглядало на проді. Другий режим прибрано: стан
 * більше нема де розійтись, і зберігати нема чого.
 */
const panelOpen = ref(false);

// Пауза перед закриттям потрібна не для миші (рельса й панель стикаються, тож
// розриву немає), а для клавіатури: `focusout` спрацьовує ДО `focusin` на
// наступному елементі, і без затримки панель згорталась би посеред Tab.
const PANEL_CLOSE_DELAY_MS = 120;
let closeTimer: ReturnType<typeof setTimeout> | null = null;

const cancelClose = () => {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
};

const openPanel = () => {
  cancelClose();
  panelOpen.value = true;
};

const closePanel = () => {
  cancelClose();
  closeTimer = setTimeout(() => {
    panelOpen.value = false;
    closeTimer = null;
  }, PANEL_CLOSE_DELAY_MS);
};

onUnmounted(cancelClose);

const close = () => {
  open.value = false;
  cancelClose();
  panelOpen.value = false;
};

const canListChats = computed(() => isGranted('ugi:bot:chat:list'));

const { result: chatTabCountsResult, refetch: refetchChatTabCounts } = useQuery(
  ListChatTabCountsDocument,
  () => ({
    enabled: canListChats.value,
    pollInterval: 30_000,
    fetchPolicy: 'cache-and-network',
  }),
);

// Instant counter updates: the 30s poll alone leaves the badge stale. The
// single ChatUpdated WS subscription lives in useChatSoundNotifications
// (App.vue) — we listen to its bridge event instead of opening a duplicate.
const onChatUpdatedBridge = () => {
  if (canListChats.value) {
    void refetchChatTabCounts();
  }
};

onMounted(() => {
  window.addEventListener('ugi:chat-updated', onChatUpdatedBridge);
});

onUnmounted(() => {
  window.removeEventListener('ugi:chat-updated', onChatUpdatedBridge);
});

const pendingChatsCount = computed(
  () => chatTabCountsResult.value?.chatTabCounts?.pending ?? 0,
);

// У заголовку й на іконці — все, що чекає на секретаря: нічийні ескалації і
// власні чати, де студент чекає на відповідь (`mineAwaitingReply`, див.
// `awaitingReplySinceSql` на сервері). Лише `pending` був неповним:
// відповідь студента у «Моїх» ніде за межами вкладки не з'являлась.
const attentionChatsCount = computed(() => {
  const counts = chatTabCountsResult.value?.chatTabCounts;

  return (counts?.pending ?? 0) + (counts?.mineAwaitingReply ?? 0);
});

// Стан «Вхідних» створюється один раз тут і роздається нащадкам (значок у
// меню, сторінка /inbox): один запит лічильника, одна жива підписка і один
// набір дій, скільки б екранів їх не читало. Без права `notifications:list`
// усередині нічого не запитується.
const notifications = useNotifications();
provideNotifications(notifications);
const {
  canList: canListNotifications,
  unreadCount: unreadNotificationsCount,
  badgeText: unreadNotificationsBadge,
} = notifications;

// У заголовку вкладки й на іконці застосунку — одне число: чати, що чекають,
// плюс непрочитане у «Вхідних». Два виклики `useAppBadge` затирали б один
// одного, тому він отримує вже готову суму.
const appBadgeCount = computed(() => attentionChatsCount.value + unreadNotificationsCount.value);

useAppBadge(appBadgeCount);

/**
 * Push, який приходить, поки вікно у фоні, ставить число на іконку сам service
 * worker. Непрочитане у «Вхідних» він дізнається з сервера, а чатів не знає
 * зовсім — тому їхню частину вікно повідомляє йому при кожній зміні.
 * Найкраще зусилля: без контролера (перше відкриття, Firefox у приватному
 * режимі) повідомляти просто нікому.
 */
const postBadgeBaseToServiceWorker = (count: number) => {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'app:badge-base', count });
  } catch {
    // Воркер міг зникнути між перевіркою і викликом — наступна зміна повторить.
  }
};

watch(attentionChatsCount, postBadgeBaseToServiceWorker);
onMounted(() => postBadgeBaseToServiceWorker(attentionChatsCount.value));

useSwNavigationListener();
useVersionCheck(pendingChatsCount);

const canViewDashboard = computed(() => isGranted('ugi:bot:chat:dashboard'));

// «Довідка» в меню — лише коли є посібник, який ця людина може читати (у
// кожного посібника своє право, і знає їх API). Сам `/help` відкритий усім: це
// запасна посадка «/».
const { result: helpGuidesResult } = useQuery(ListHelpGuidesDocument, () => ({ fetchPolicy: 'cache-first' }));
const hasHelpGuides = computed(() => (helpGuidesResult.value?.helpGuides.length ?? 0) > 0);

/**
 * Linear renders a collapsible sidebar section («Workspace», «Your teams») as a
 * quiet 12px label with a chevron — not as a nav item with its own icon. Giving
 * the group headers an icon, as we did, made them compete with their own
 * children for attention: two icon columns at two indents, both at item weight.
 *
 * Nuxt UI merges `item.class` into the `link` slot last, so these win over the
 * shared `navigationUi.link` metrics without needing a second component.
 */
const GROUP_TRIGGER_CLASS
  = 'h-6 mt-3 px-2 text-xs font-medium text-muted hover:text-toned';

/**
 * Тут — навігація ЗАГАЛЬНА: те, чим користуються різні ролі. Сімнадцять екранів
 * розкладу з цього меню пішли у власну шапку модуля
 * (`schedule/components/ScheduleSubNav.vue`).
 *
 * Межу проведено за правами, а не за URL: жодного з дозволів на `/schedule/*`
 * немає в секретаря — це цілком диспетчерський домен, і в спільному меню він
 * лише розсовував групи. У сайдбарі від нього лишився один пункт «Розклад» на
 * рельсі: диспетчер потрапляє в модуль одним кліком, далі навігація в шапці.
 *
 * Виняток, який СВІДОМО лишився тут: «Навчальні роки» й «Навчальний календар».
 * Розкладу вони потрібні, але це `study-periods`, а їх має і секретар — дати
 * семестру йдуть у довідки. Спільний екран не може жити всередині модуля.
 *
 * Порядок груп — це порядок будівництва: спершу те, чим інститут Є, далі те,
 * що треба викладати.
 */
// `data-testid` їде на корінь `UChip` через `v-bind` — інакше крапку на рельсі
// нема за що взяти в тесті.
const INBOX_RAIL_CHIP: NavigationMenuItem['chip'] = Object.assign(
  { color: 'primary' as const },
  { 'data-testid': 'rail-inbox-badge' },
);

interface NavGroup {
  id: string;
  label: string;
  items: NavigationMenuItem[];
}

const link = (
  label: string,
  icon: string,
  to: string,
  slot?: string,
): NavigationMenuItem => ({ label, icon, to, ...(slot ? { slot } : {}), onSelect: close });

/**
 * Пункти, які видно на рельсі без наведення.
 *
 * Це не «найважливіші взагалі», а найчастіші: 8 із 12 співробітників —
 * секретарі, і їхній робочий день — це рівно Підтримка, Документи, Студенти.
 * Решта екранів живе в секціях за одним наведенням.
 *
 * «Розклад» тут — не виняток із цього правила, а той самий принцип для
 * диспетчера: він в інституті один, і весь його день — цей модуль. Раніше
 * жоден із дев'яти пунктів розкладу на рельсі не стояв, і кожен його перехід
 * ішов через наведення. Тепер один пункт веде в модуль, а всередині працює
 * власна навігація в шапці.
 */
const primaryItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = [];

  if (canViewDashboard.value) {
    items.push(link('Аналітика', 'i-lucide-layout-dashboard', '/dashboard', 'dashboard'));
  }

  if (canListChats.value) {
    items.push(link('Підтримка', 'i-lucide-headset', '/chats', 'support'));
  }

  if (isGranted('ugi:core:documents:list')) {
    items.push(link('Документи', 'i-lucide-file-text', '/documents'));
  }

  if (isGranted('ugi:core:students:list')) {
    items.push(link('Студенти', 'i-lucide-graduation-cap', '/students'));
  }

  // Дошка задач деканату — щоденний інструмент тих самих людей, що працюють
  // з документами й чатами, тому стоїть на рельсі поруч із ними, а не в секції.
  if (isGranted('ugi:core:issues:list')) {
    items.push(link('Задачі', 'i-lucide-square-kanban', '/issues', 'issues'));
  }

  // Сповіщення про ті самі задачі: кому призначили, що змінило статус. Стоїть
  // одразу під дошкою, бо без неї сюди нічого не приходить.
  if (canListNotifications.value) {
    items.push(link('Вхідні', 'i-lucide-inbox', '/inbox', 'inbox'));
  }

  // `lessons:update`, а не `:list`. Пари читають усі, кого вони стосуються:
  // студент бачить свої, викладач — свої, і обидва мають `lessons:get|list`
  // заради власного розкладу в боті та вебапці. Але цей пункт веде в РЕДАКТОР
  // розкладу, а редагує його тільки диспетчер, тож питати треба про право
  // редагувати. Так само вирішує «/» (`core/router/index.ts`) — там ця межа
  // вже проведена, і тут вона мала бути тією самою від початку.
  if (isGranted('ugi:core:lessons:update')) {
    items.push(link('Розклад', 'i-lucide-calendar-clock', '/schedule', 'schedule'));
  }

  if (hasHelpGuides.value) {
    items.push(link('Довідка', 'i-lucide-circle-help', '/help', 'help'));
  }

  return items;
});

const groups = computed<NavGroup[]>(() => {
  // --- Деканат: усе, чим займається секретар, крім винесеного на рельсу ---
  const dean: NavigationMenuItem[] = [];

  if (isGranted('ugi:core:orders:list')) {
    dean.push({
      label: 'Книга наказів',
      icon: 'i-lucide-scroll-text',
      to: '/orders',
      onSelect: close,
    });
  }

  // Студенти й групи переїхали сюди зі «Структури». Ними веде деканат: вступ,
  // переведення, відрахування. Диспетчерові вони потрібні як довідник усередині
  // форм (склад когорти, вибір групи); з 28.10.2026 він має `students:list`, тож
  // бачить і ці пункти — лише для перегляду, без картки студента.
  //
  // «Студенти» звідси піднялись на рельсу, ця сторінка лишилась тут — вона
  // потрібна на порядок рідше.
  //
  // Назва пройшла три кроки. «Випускники та відраховані» — це заголовок
  // сторінки, і там він доречний, але в меню перелічує два стани замість того,
  // щоб назвати сутність; до того ж він удвічі довший за будь-якого сусіда.
  // «Колишні» вирішувало довжину і створювало гіршу проблему: прикметник без
  // іменника серед сусідів-іменників читається як обрізок, а перша асоціація
  // до «колишніх» в українській — побутова, не про навчання.
  //
  // «Колишні студенти» — повна іменникова група, чесна для обох станів (і
  // випускник, і відрахований — колишні студенти) і читається в парі зі
  // «Студентами» на рельсі як теперішні / колишні. «Випускники» самі по собі
  // були б неправдою: за замовчуванням сторінка показує й відрахованих.
  if (isGranted('ugi:core:students:list')) {
    dean.push(link('Колишні студенти', 'i-lucide-user-check', '/completed-studies'));
  }

  // Гейт саме на `:get`, а не на `:list`.
  //
  // `groups:list` — це право підвантажити перелік груп у поле фільтра, і його
  // має навіть секретар бухгалтерії, який далі списку студентів не ходить.
  // Право відкрити картку групи (`:get`) є в того, хто з довідником справді
  // працює: секретар деканату й диспетчер. Пункт меню веде саме туди, тож і
  // питає саме про це.
  if (isGranted('ugi:core:groups:get')) {
    dean.push(link('Групи', 'i-lucide-users-round', '/groups'));
  }

  // Той самий випадок, що й з розкладом: `knowledgebase-articles:list` має і
  // студент — він читає базу знань у боті, — а цей розділ є її редактором.
  // Веде базу деканат, тож гейт на `:update`.
  if (isGranted('ugi:core:knowledgebase-articles:update')) {
    dean.push(link('База знань', 'i-lucide-book-open', '/knowledgebase'));
  }

  // --- Інститут: довідники, які живуть довше за семестр ---
  const institute: NavigationMenuItem[] = [];

  if (isGranted('ugi:core:teachers:list')) {
    institute.push(link('Викладачі', 'i-lucide-presentation', '/teachers'));
  }

  // Те саме, що й з групами: `programs:list` живить поле фільтра в списку
  // студентів, а сам розділ відкриває той, хто має `programs:get`.
  if (isGranted('ugi:core:programs:get')) {
    institute.push(link('Освітні програми', 'i-lucide-badge-check', '/programs'));
  }

  if (isGranted('ugi:core:departments:list')) {
    institute.push(link('Кафедри', 'i-lucide-landmark', '/departments'));
  }

  // Навчальний рік і календар — це study-periods, а на них права має і
  // секретар (дати семестру потрібні для довідок), і диспетчер.
  //
  // Гейт на `:list` — на календарі інституту цілком, а не на `:get`, яким
  // студент дивиться власні періоди у вебапці. Різниця не косметична: поки
  // студент мав ще й `:list`, ці два пункти показувались кожному, кому крім
  // студентської ролі дали службову.
  if (isGranted('ugi:core:study-periods:list')) {
    institute.push(link('Навчальні роки', 'i-lucide-calendar-cog', '/academic-calendar/years'));
    institute.push(link('Навчальний календар', 'i-lucide-calendar-days', '/academic-calendar'));
  }

  // --- Адміністрування: те, що чіпають раз на семестр або рідше ---
  const administration: NavigationMenuItem[] = [];

  if (isGranted('ugi:core:users:list')) {
    administration.push(link('Користувачі', 'i-lucide-users', '/users'));
  }

  // Журнал змін — хто, що й звідки змінив у записах про студентів. Право є
  // лише в суперадміна (рішення власника 2026-10-19): у журналі лежить повний
  // стан видалених записів.
  if (isGranted('ugi:core:audit-log:list')) {
    administration.push(link('Журнал змін', 'i-lucide-history', '/audit', 'audit-log'));
  }

  return [
    { id: 'dean', label: 'Деканат', items: dean },
    { id: 'institute', label: 'Інститут', items: institute },
    { id: 'administration', label: 'Адміністрування', items: administration },
  ].filter((group) => group.items.length > 0);
});

/**
 * Яка група відповідає поточному екрану — щоб решта стартувала згорнутою.
 *
 * Збіг шукається за НАЙДОВШИМ префіксом, а не за першим: `/academic-calendar`
 * підходить і під «Навчальний календар», і під «Навчальні роки»
 * (`/academic-calendar/years`), і правильна відповідь — довша. Зараз обидва в
 * одній групі, тож правило нічого не міняє, — воно лишається, бо наступний
 * вкладений маршрут інакше показав би не ту секцію.
 *
 * `null` означає «жодна група», і тоді розкриті всі. Це свідомо: схема
 * «відкрита лише активна» вже ховала від диспетчера, який стоїть на дашборді,
 * саме те, заради чого він тут.
 *
 * Спроба замінити це правило на «згорнуті всі» була і провалилась: диспетчер
 * на дашборді не бачив жодного свого екрана. Жоден e2e цього не стереже —
 * `user-multi-role.spec.ts` перевіряє лише `nav-link-support` і
 * `nav-link-schedule`, а вони обидва з `primaryItems`, який рендериться поза
 * групами і на `defaultOpen` не реагує. Тобто змінити рядок нижче на `false`
 * можна, і сюїта промовчить. Прокручування дешевше за третій клік до головного
 * розділу.
 */
const activeGroupId = computed<string | null>(() => {
  let bestId: string | null = null;
  let bestLength = 0;

  for (const group of groups.value) {
    for (const item of group.items) {
      const to = typeof item.to === 'string' ? item.to : null;

      if (to && route.path.startsWith(to) && to.length > bestLength) {
        bestId = group.id;
        bestLength = to.length;
      }
    }
  }

  return bestId;
});

// На рельсі — самі іконки винесених пунктів; підписи їм дає тултип.
//
// Лічильник у згорнутому меню Nuxt UI ховає (`linkTrailing: hidden`), тож про
// непрочитане у «Вхідних» рельса каже крапкою на іконці, а число живе в панелі
// й у заголовку вкладки.
const railItems = computed<NavigationMenuItem[][]>(() => [
  primaryItems.value.map((item) => (
    item.slot === 'inbox' && unreadNotificationsCount.value > 0
      ? { ...item, chip: INBOX_RAIL_CHIP }
      : item
  )),
]);

// У панелі — ті самі п'ять із підписами, а під ними секції.
const panelItems = computed<NavigationMenuItem[][]>(() => {
  const sections = groups.value.map((group): NavigationMenuItem[] => [{
    label: group.label,
    children: group.items,
    defaultOpen: activeGroupId.value === null || activeGroupId.value === group.id,
    class: GROUP_TRIGGER_CLASS,
  }]);

  return [primaryItems.value, ...sections];
});

/**
 * Метрики зняті з живого Linear (getComputedStyle), а не підібрані на око:
 *
 *   пункт меню   h=28px, radius 8px, padding 0 8px, 13px / 500
 *   підпис секції 12px / 500, той самий muted-колір, БЕЗ великих літер
 *   проміжок     ~1px — у Linear пункти стоять майже впритул
 *
 * Головна зміна проти попереднього варіанта — щільність. Було 36px на пункт
 * і gap-1.5 між ними; у сайдбар вміщалося ~14 рядків, і половина навігації
 * жила за межами екрана. Тепер вміщається ~22 при тій самій читабельності:
 * 13px/500 на майже чорному тлі читається не гірше за 14px/450.
 */
const navigationUi = {
  root: 'gap-px',
  list: 'flex flex-col gap-px',
  // Linear пише підписи секцій звичайним регістром. Верхній регістр із
  // розрядкою — «адмінка з коробки»: він кричить там, де має підказувати.
  label: 'text-xs font-medium text-muted px-2 pt-3 pb-1',
  // У сайдбарі Linear немає ЖОДНОЇ лінії — ні горизонтальних роздільників між
  // секціями, ні направляючої під розкритою групою. Групи тримає порожнє місце
  // й нічого більше, і саме тому сайдбар читається як список, а не як таблиця.
  //
  // Nuxt UI малює роздільник між кожним масивом у `items` (а вони в нас на
  // групу), тож вимкнути його можна лише тут.
  separator: 'hidden',
  link: 'gap-2 text-sm font-medium rounded-lg px-2 h-7 before:inset-0 before:rounded-lg',
  linkLeadingIcon: 'shrink-0 size-4',
  linkLabel: 'truncate',
  linkTrailingIcon: 'size-3.5 text-dimmed',
  // Без відступу — так само, як у Linear. Заміряно в них на живому сайдбарі:
  //
  //   підпис секції «Workspace»   текст на x=21
  //   пункт під ним «Projects»    ІКОНКА на x=21
  //
  // Тобто пункт прямо під підписом секції не зсунутий узагалі: його іконка
  // стоїть рівно там, де починається текст підпису. Відступ у Linear отримує
  // лише справжній третій рівень (екрани всередині команди: «Ugi» → «Home»),
  // а в нас рівнів два, тож наші пункти — це аналог «Projects», не «Home».
  //
  // `border-s-0` обов'язковий окремо: `border-s` сидить у базовій темі
  // Nuxt UI, і просто прибрати його зі свого рядка недостатньо.
  childList: 'ms-0 ps-0 border-s-0',
  // `ps-1.5 -ms-px` у базовій темі існують лише щоб дати місце вертикальній
  // направляючій, якої в нас більше немає, — без них лишається зайвих 5px.
  childItem: 'min-w-0 ps-0 ms-0',
  childLink: 'gap-2 text-sm font-medium rounded-lg px-2 h-7 min-w-0 before:inset-0 before:rounded-lg',
  childLinkIcon: 'shrink-0 size-4',
  childLinkLabel: 'truncate',
};

const { preference } = useTheme();

const THEME_LABELS = {
  light: 'Світла',
  dark: 'Темна',
  system: 'Як у системі',
} as const;

const userMenuItems = computed(() => [[{
  label: 'Мій профіль',
  icon: 'i-lucide-user',
  to: '/me/edit',
}, {
  label: 'Сповіщення',
  icon: 'i-lucide-bell',
  to: '/me/notifications',
},
// Свої токени UGI MCP — у кого є що робити через MCP (право видати токен).
...(isGranted('ugi:core:mcp-tokens:list')
  ? [{ label: 'MCP-токени', icon: 'i-lucide-key-round', to: '/me/mcp-tokens', slot: 'mcp-tokens' as const }]
  : [])], [{
  label: 'Оформлення',
  icon: 'i-lucide-sun-moon',
  children: (['light', 'dark', 'system'] as const).map((value) => ({
    label: THEME_LABELS[value],
    type: 'checkbox' as const,
    checked: preference.value === value,
    onSelect: () => {
      preference.value = value;
    },
  })),
}], [{
  label: 'Вийти',
  icon: 'i-lucide-log-out',
  onSelect: () => logout(),
}]]);
</script>

<template>
  <!-- Critical error: hide entire layout, show only error -->
  <template v-if="hasError && isCriticalError">
    <UErrorView />
  </template>

  <!-- Normal layout -->
  <template v-else>
    <!--
      Ширина рельси береться з `min-w-12`, а НЕ з розміру сайдбара: тут
      `default-size` нуль, тож `--width` теж нуль.

      Ні `collapsible`, ні `resizable` — і це не косметика. `useResizable`
      вмикає збереження стану умовою `persistent && (resizable || collapsible)`,
      а ширину малює з ЗБЕРЕЖЕНОГО `size`, не з `collapsedSize`:

        :style="{ '--width': `${size || 0}${unit}` }"

      Тобто з `collapsible` компонент читав із localStorage власний ключ
      `dashboard-sidebar-main` (його імʼя збирається з `id`), знаходив там
      `{"size":12,"collapsed":false}` від колишнього закріпленого стану — і
      малював контейнер на 12rem = 192px, поки вміст усередині лишався
      згорнутим. Саме так це виглядало на проді: вузька смужка іконок і
      порожнеча до самого контенту.

      Свій ключ (`main-sidebar-collapsed`) ми прибрали раніше, але це другий,
      чужий, і він живе за іншим імʼям. Без цих двох прапорців сховище не
      читається взагалі, тож жоден залишок у браузері вже нічого не змінює.
    -->
    <UDashboardSidebar
      id="main"
      v-model:open="open"
      :default-size="0"
      :collapsed="true"
      class="bg-muted border-e-0"
      data-testid="sidebar-rail"
      :ui="{ root: 'min-w-12', header: 'h-11 px-2', body: 'px-2 py-1' }"
      @mouseenter="openPanel"
      @mouseleave="closePanel"
      @focusin="openPanel"
      @focusout="closePanel"
    >
      <template #header>
        <UDropdownMenu
          :items="userMenuItems"
          :ui="{ content: 'min-w-56' }"
        >
          <template #mcp-tokens-label="{ item }">
            <span data-testid="user-menu-mcp-tokens">{{ item.label }}</span>
          </template>
          <UButton
            color="neutral"
            variant="ghost"
            class="w-full"
            data-testid="sidebar-user-button"
            :ui="{ base: 'gap-2 px-2 h-7 rounded-lg' }"
          >
            <UAvatar
              :src="currentUser.avatar?.renditions?.[0]?.url"
              :alt="currentUser.fullName"
              size="2xs"
            />
          </UButton>
        </UDropdownMenu>
      </template>

      <!-- Тултип лишається тільки як запасний шлях: панель відкривається тим
           самим наведенням, тож поверх неї він дублював би підпис. -->
      <template #default>
        <UNavigationMenu
          :collapsed="true"
          :items="railItems"
          :ui="navigationUi"
          orientation="vertical"
          :tooltip="!panelOpen"
        >
          <template #support-trailing>
            <UBadge
              v-if="pendingChatsCount > 0"
              color="error"
              variant="solid"
              size="sm"
              square
              :label="pendingChatsCount > 99 ? '99+' : String(pendingChatsCount)"
              :ui="{ base: 'rounded-full min-w-5 justify-center' }"
            />
          </template>
        </UNavigationMenu>
      </template>
    </UDashboardSidebar>

    <!--
      Панель, що виїжджає з рельси. Живе поверх контенту (`absolute`), а не
      поруч із ним, тому ширина контентної області не змінюється — це і є
      єдина причина, чому рельса взагалі щось економить.

      Панель завжди в DOM: інакше вона не потрапляла б у порядок табуляції
      одразу за рельсою, і секції були б доступні лише мишею — на самій рельсі
      їх немає.

      Тому в закритому стані `inert` + `pointer-events-none`: без них
      прозора смуга у 224px ловила б кліки по контенту під собою.

      Анімація на класах, а не через `<Transition>`: із `v-show` перехід
      застрягав у `v-leave-from` — Vue не отримував `transitionend` і не
      доводив елемент до `display: none`, тож він лишався клікабельним.

      Панель починається з нуля і НАКРИВАЄ рельсу, а не стає праворуч від неї
      (так у Productlane). Її іконки лягають рівно на місце іконок рельси, а
      підкладка та сама, тож рух читається як «рельса розширилась». Якщо
      поставити панель поруч, на екрані виходять дві смуги з подвоєними
      іконками. З тієї ж причини анімується лише прозорість: будь-який зсув
      зрушив би іконки, які мають лишитись на місці.
    -->
    <div
      class="absolute inset-y-0 start-0 z-50 hidden w-56 flex-col bg-muted shadow-2xl ring-1 ring-default
        transition-opacity duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] lg:flex"
      :class="panelOpen ? 'opacity-100' : 'pointer-events-none opacity-0'"
      :inert="!panelOpen"
      :aria-hidden="!panelOpen"
      data-testid="sidebar-hover-panel"
      @mouseenter="openPanel"
      @mouseleave="closePanel"
      @focusin="openPanel"
      @focusout="closePanel"
    >
      <!--
        Шапка повторює шапку сайдбара один-в-один: ті самі відступи, той самий
        розмір аватара. Панель лежить поверх рельси, тож аватар має опинитись
        рівно там, де вже стояв, — інакше при відкритті він стрибне.

        Підпис імені живе тільки тут: на рельсі його нема куди подіти, тож
        `data-testid` не дублюється ні з чим.
      -->
      <div class="flex h-11 shrink-0 items-center gap-1 px-2">
        <UDropdownMenu
          :items="userMenuItems"
          :ui="{ content: 'min-w-56' }"
          class="min-w-0 flex-1"
        >
          <template #mcp-tokens-label="{ item }">
            <span data-testid="user-menu-mcp-tokens">{{ item.label }}</span>
          </template>
          <UButton
            color="neutral"
            variant="ghost"
            class="w-full"
            data-testid="sidebar-panel-user-button"
            :ui="{ base: 'gap-2 px-2 h-7 rounded-lg', trailingIcon: 'ms-auto size-3.5 text-dimmed' }"
            trailing-icon="i-lucide-chevron-down"
          >
            <UAvatar
              :src="currentUser.avatar?.renditions?.[0]?.url"
              :alt="currentUser.fullName"
              size="2xs"
            />
            <span
              class="truncate text-sm font-medium text-highlighted"
              data-testid="sidebar-user-name"
            >
              {{ currentUser.fullName }}
            </span>
          </UButton>
        </UDropdownMenu>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto px-2 py-1">
        <UNavigationMenu
          :items="panelItems"
          :ui="navigationUi"
          orientation="vertical"
        >
          <template #dashboard-label="{ item }: { item: NavigationMenuItem }">
            <span data-testid="nav-link-dashboard">{{ item.label }}</span>
          </template>
          <template #support-label="{ item }: { item: NavigationMenuItem }">
            <span data-testid="nav-link-support">{{ item.label }}</span>
          </template>
          <template #schedule-label="{ item }: { item: NavigationMenuItem }">
            <span data-testid="nav-link-schedule">{{ item.label }}</span>
          </template>
          <template #issues-label="{ item }: { item: NavigationMenuItem }">
            <span data-testid="nav-link-issues">{{ item.label }}</span>
          </template>
          <template #inbox-label="{ item }: { item: NavigationMenuItem }">
            <span data-testid="nav-link-inbox">{{ item.label }}</span>
          </template>
          <template #audit-log-label="{ item }: { item: NavigationMenuItem }">
            <span data-testid="nav-link-audit-log">{{ item.label }}</span>
          </template>
          <template #help-label="{ item }: { item: NavigationMenuItem }">
            <span data-testid="nav-link-help">{{ item.label }}</span>
          </template>
          <template #support-trailing>
            <UBadge
              v-if="pendingChatsCount > 0"
              color="error"
              variant="solid"
              size="sm"
              square
              :label="pendingChatsCount > 99 ? '99+' : String(pendingChatsCount)"
              :ui="{ base: 'rounded-full min-w-5 justify-center' }"
            />
          </template>
          <template #inbox-trailing>
            <UBadge
              v-if="unreadNotificationsCount > 0"
              color="primary"
              variant="solid"
              size="sm"
              square
              :label="unreadNotificationsBadge"
              :ui="{ base: 'rounded-full min-w-5 justify-center' }"
              data-testid="nav-inbox-badge"
            />
          </template>
        </UNavigationMenu>
      </div>
    </div>

    <!--
      min-w-0 is REQUIRED: this is the flex child next to the dashboard sidebar.
      Without it the item keeps its `min-width: auto` (= content min-width), so a
      wide child (a data table with long cells — emails, UUIDs) grows the whole
      content column past the viewport and the parent's `overflow-hidden` clips
      the right edge (toolbar buttons, last table columns). `min-w-0` lets the
      column shrink to the available space so tables stay inside the content area
      and scroll internally instead. Fixes table overflow on every admin page.

      The rounded, inset panel is Linear's shell: the app background is the
      DARKEST surface (the sidebar sits directly on it, borderless) and the
      content floats above it one step lighter, separated by a gap instead of a
      divider line. That inversion — dark chrome, lighter content — is most of
      why Linear reads as a product where a stock dashboard reads as a
      template. Desktop-only: on a phone the panel IS the screen.
    -->
    <div
      class="w-full min-w-0 h-svh flex flex-col bg-default overflow-hidden lg:h-[calc(100svh-0.75rem)] lg:my-1.5 lg:me-1.5 lg:rounded-xl lg:border lg:border-default"
    >
      <!-- Non-critical error: show within layout -->
      <UErrorView v-if="hasError" />
      <RouterView v-else />
    </div>
  </template>
</template>
